import { PercentPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  effect
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { DUAL_CURRENCY_DEPOSIT } from '@approot/constants';
import {
  AttributeBase,
  DiscountFactorResponse,
  Leg,
  LegArray,
  LegArrayItem,
  LegSyncingFields,
  RfqLegSettings,
  RfqTabLegs
} from '@approot/models';
import {
  AdminService,
  AttributeControlService,
  ConfigService,
  ExecutionContext,
  LegService,
  ProductsConfigService,
  RfqDetails,
  RfqService,
  RfqTabService,
  VolBasedRfqOptionPriceV2Result,
  canDisplayDF
} from '@approot/services';
import {
  SubscriptionSink,
  Utilities,
  clone,
  reformatDate,
  sleep
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import {
  markFieldAsPending,
  setAttributeReadOnly
} from '@approot/widgets/strategy-detail/leg/utilities/leg.utilities';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { EMPTY, Subscription, firstValueFrom, forkJoin, merge, of } from 'rxjs';
import { debounceTime, repeat, switchMap, tap } from 'rxjs/operators';
import {
  Action,
  VolBasedRfqOptionPriceV2Hedge,
  VolBasedRfqOptionPriceV2Request
} from '../provider-quote-prices/provider-quote-types';
import {
  GetLiquidityProviderEventsResponse,
  ProviderQuoteService,
  TabItem
} from '../provider-quote.service';
import { ProviderQuoteSetupComponent } from './../provider-quote-setup/provider-quote-setup.component';
const { cleanFormattedNumeric } = Utilities;

@Component({
  selector: 'app-provider-quote-tab',
  templateUrl: './provider-quote-tab.component.html',
  styleUrls: ['./provider-quote-tab.component.scss'],
  providers: [LegService, RfqTabService]
})
export class ProviderQuoteTabComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() public rfqDetails: RfqDetails;
  @Input() public tabItem: TabItem;
  @Output() public closeTab = new EventEmitter<number>();
  @ViewChildren(LegComponent) public legComponents: QueryList<LegComponent>;
  @ViewChild('setup', { static: false })
  setupComponent: ProviderQuoteSetupComponent;
  public legs: RfqTabLegs[] = [];
  public rfqLegSettings: RfqLegSettings = {
    rfqLeg: true,
    editableSettings: { editableFields: [], lockWhileActive: [] },
    static: true,
    providerLeg: true
  };
  public legSyncingInfo: LegSyncingFields = {
    spot: false,
    forwardPoints: false,
    premiumValueType: false,
    premiumSettlementDate: false
  };
  public volBasedRfqOptionPriceResult: VolBasedRfqOptionPriceV2Result;
  public pricesLoading = false;
  public initialPrice = true;
  public cross: string;
  public setupForm: FormGroup;
  public showTab = false;
  public failedExecutionContext = false;
  public forwardPointsMultiplier: number;
  public pipsHighlightedIndices: number[] = [];
  public nestedLegs = [];
  public showExpiryDate = false;
  private prevVolBasedRfqOptionPrice: VolBasedRfqOptionPriceV2Request;
  private volBasedRfqOptionPriceSub: Subscription;
  private blockPricing = false;
  private subSink = new SubscriptionSink();
  private lpEventsSub: Subscription;
  private wlQuoteEventsSub: Subscription;
  constructor(
    private adminService: AdminService,
    private legService: LegService,
    private productsConfigService: ProductsConfigService,
    private rfqService: RfqService,
    private rfqTabService: RfqTabService,
    private percentPipe: PercentPipe,
    private attributeControlService: AttributeControlService,
    private providerQuoteService: ProviderQuoteService,
    private configService: ConfigService
  ) {
    effect(_ => {
      switch (this.rfqTabService.forwardPointsMultiplier()) {
        case 100:
          this.pipsHighlightedIndices = [0, 1];
          break;
        case 10_000:
          this.pipsHighlightedIndices = [2, 3];
          break;
        default:
          this.pipsHighlightedIndices = [];
          break;
      }
    });

    this.legService.productSpecifications = clone(
      this.productsConfigService.productInfo.productSpecifications
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.rfqDetails) {
      console.log('RFQ Details changed [ProviderQuoteTabComponent]');
      this.rfqDetailsChange();
    }
  }

  public ngAfterViewInit(): void {
    this.subSink.sink = this.legComponents.changes.subscribe(legComponents => {
      this.callVolBasedRfqOptionOnLegChange();
      this.rfqTabService.selectedLegs = legComponents.toArray();
    });
  }

  public ngOnDestroy(): void {
    this.volBasedRfqOptionPriceSub?.unsubscribe();
    this.subSink.unsubscribe();
  }

  public async ngOnInit() {
    if (!this.rfqDetails) {
      console.warn('Missing RFQ Details [ProviderQuoteTabComponent]');
    }
    try {
      const executionContext = await this.getExecutionContext();
      const executionType = executionContext.executionTypes[0].shortName;
      this.tabItem.isVolBased = executionType === 'volBased';
      this.tabItem.isPremiumBasedVanillasWithSpotFeed =
        executionType === 'premiumBasedVanillasWithSpotFeed';
      this.tabItem.isPremiumBased = executionType === 'premiumBased';
      this.tabItem.executionContext = executionContext;
    } catch (error) {
      console.error(error);
      this.failedExecutionContext = true;
      return;
    }
    this.setupProviderTab();
  }

  public doCloseTab() {
    this.closeTab.emit();
  }

  public get isMedusaAccountLoggedIn() {
    return this.adminService.isMedusaAccountLoggedIn;
  }

  public onLegPresetLoaded(index: number): void {
    const legComponent = this.legComponents.toArray()[index];
    legComponent.legFwdVolService.initForwardPointVolCalls();
    if (
      (this.tabItem.isPremiumBasedVanillasWithSpotFeed ||
        this.tabItem.isVolBased) &&
      index === this.legComponents.length - 1
    ) {
      this.getDf();
    }
  }

  public quoteEventActionChange(action: Action): void {
    switch (action) {
      case 'CANCELLED':
      case 'ConfirmRFEAccept':
      case 'Unsupported':
      case 'LoggedOff':
      case 'DealCancelled':
      case 'ConfirmRFEError':
        if (this.tabItem.isPremiumBasedVanillasWithSpotFeed) {
          this.blockPricing = true;
          this.volBasedRfqOptionPriceSub?.unsubscribe();
          this.setupComponent?.unsubscribeFromSpot();
        }
        this.wlQuoteEventsSub?.unsubscribe();
        this.lpEventsSub?.unsubscribe();
        break;
      default:
        break;
    }
  }

  public changeInSetupForm(): void {
    this.volBasedRfqOptionPrice();
  }

  public changeInLeg(): void {
    if (this.tabItem.isPremiumBasedVanillasWithSpotFeed) {
      this.volBasedRfqOptionPrice();
    }
  }

  public get rfqComment(): string {
    return this.setupComponent?.rfqComment;
  }

  private handlePremiumBasedWithSpotFeed(): void {
    if (this.tabItem.isPremiumBasedVanillasWithSpotFeed) {
      if (this.tabItem.fullQuote.quote.quoteStatus === 'Declined') {
        this.blockPricing = true;
        return;
      }
      this.legSyncingInfo.forwardPoints = true;
      this.setExtraFieldValidation();
    }
  }

  private handleEditableFields(): void {
    if (this.tabItem.isVolBased) {
      this.addEditableFields(['volBid', 'volAsk', 'spot']);
    }

    if (this.tabItem.isPremiumBasedVanillasWithSpotFeed) {
      this.addEditableFields([
        'forwardPointsBid',
        'forwardPointsAsk',
        'discountFactor',
        'volBid',
        'volAsk'
      ]);
    }
  }

  private addEditableFields(fields: string[]): void {
    const filteredFields = fields.filter(
      field =>
        !this.rfqLegSettings.editableSettings.editableFields.includes(field)
    );
    this.rfqLegSettings.editableSettings.editableFields.push(...filteredFields);
  }

  private setExtraFieldValidation(): void {
    this.tabItem.legs.forEach(tabItemLeg => {
      this.rfqLegSettings.editableSettings.editableFields.forEach(field => {
        let attributeToUse: AttributeBase<any> = null;
        tabItemLeg.leg.leg.attributesAsFormControls.find(attributeCtrl => {
          if (attributeCtrl.attributes?.length) {
            return attributeCtrl.attributes.find(innerAttribute => {
              if (innerAttribute.key === field) {
                attributeToUse = innerAttribute;
              }
              return innerAttribute.key === field;
            });
          } else if (attributeCtrl.key === field) {
            attributeToUse = attributeCtrl;
          }
          return attributeCtrl.key === field;
        });
        if (!attributeToUse) {
          return;
        }
        attributeToUse.required = true;
        const validators = attributeToUse.validator;
        switch (field) {
          case 'volBid':
          case 'volAsk':
            if (!validators.includes('percentage')) {
              validators.push('percentage');
            }
            break;
          default:
            break;
        }
      });
    });
  }

  private getDf(): void {
    const legComponents = this.legComponents.toArray();
    legComponents.forEach(x => {
      markFieldAsPending(x.form, x.leg, 'discountFactor');
    });
    const products = this.legService.createProductsObject(
      legComponents.map(legComponent => {
        return {
          product: legComponent.form.value,
          attributes: legComponent.leg.attributes
        };
      })
    );

    forkJoin(
      legComponents.map(x => {
        if (canDisplayDF(x.leg.productType)) {
          markFieldAsPending(x.form, x.leg, 'discountFactor');
          return this.rfqTabService.discountFactor(
            this.legService.createProductsObject([
              {
                product: x.form.value,
                attributes: x.leg.attributes
              }
            ])[0]
          );
        }
        return of(null);
      })
    )
      .pipe(
        tap((responses: DiscountFactorResponse[]) => {
          responses.forEach(
            (response: DiscountFactorResponse, index: number) => {
              const legComponent = legComponents[index];
              if (response) {
                this.handleLegDf(legComponents[index], response);
              }
              setAttributeReadOnly(
                'discountFactor',
                false,
                legComponent.leg,
                legComponent.form,
                legComponent.rfqExecutionSettings
              );
            }
          );
        })
      )
      .subscribe();
  }

  private handleLegDf(
    legComponent: LegComponent,
    response: DiscountFactorResponse
  ): void {
    const form = legComponent.form;
    const discountFactorCurrency = form.get('discountFactorCurrency');
    const discountFactor = form.get('discountFactor');
    discountFactorCurrency.setValue(response.currency, {
      emitEvent: false
    });

    if (!discountFactor.value) {
      discountFactor.setValue(
        this.percentPipe.transform(
          response.spotToForwardDiscountFactor[response.currency],
          '1.4-4'
        )
      );
    }
    setAttributeReadOnly(
      'discountFactor',
      false,
      legComponent.leg,
      legComponent.form,
      legComponent.rfqExecutionSettings
    );
  }

  private getVolBasedRfqOptionPrice(): VolBasedRfqOptionPriceV2Request {
    const premiumDate = dayjs
      .utc(this.tabItem.fullQuote.quote.premiumDate)
      .format('YYYY-MM-DD');
    const premiumValue = this.tabItem.fullQuote.quote.premiumDelivery as string;
    const products = this.legService.createProductsObject(
      this.legComponents.toArray().map(legComponent => {
        return {
          product: { ...legComponent.form.value },
          attributes: legComponent.leg.attributes
        };
      })
    );

    if (!this.rfqService.validateProducts(products)) {
      console.warn('Invalid product - aborting pricing');
      return null;
    }

    const hedgeMode = this.getHedgeMode();
    const hedges = hedgeMode === 'None' ? [] : this.getHedges();

    return {
      task: 'volBasedRfqOptionPrice_v2',
      premiumDate,
      premiumValue,
      premiumCurrency: this.tabItem.fullQuote.quote.premiumCurrency,
      hedgeMode,
      hedges,
      products,
      requestId: this.tabItem.requestId,
      ...this.setupForm.get('spots').value
    };
  }

  private getHedgeMode(): string {
    let hedgeMode = this.tabItem.isVolBased
      ? 'None'
      : this.setupComponent.hedge;

    if (hedgeMode !== 'None') {
      hedgeMode = 'DX';
    }
    return hedgeMode;
  }

  private getHedges(): VolBasedRfqOptionPriceV2Hedge[] {
    const quote = this.tabItem.fullQuote.quote;
    const hedgesCtrl = this.setupForm.get('hedges') as FormArray;
    const notionalCtrl = hedgesCtrl.controls[0]?.get('notional');
    const strikeCtrl = hedgesCtrl.controls[0]?.get('strike');
    const positionCtrl = hedgesCtrl.controls[0]?.get('position');
    const settlementCtrl = hedgesCtrl.controls[0]?.get('settlementDate');
    const settlementDate = reformatDate(
      settlementCtrl?.value ||
        (quote.request.hedge === 'DeltaSpot'
          ? quote.spotDate
          : this.tabItem.originalLegs[0].settlementDate),
      'YYYY-MM-DD'
    );

    let expiryDate: string = undefined;
    this.showExpiryDate = false;

    let hedgeType: 'ndf' | 'spot' | 'forward';
    if (quote.request.hedge === 'DeltaSpot') {
      hedgeType = 'spot';
    } else if (quote.cashSettled) {
      hedgeType = 'ndf';
      expiryDate =
        hedgesCtrl.controls[0]?.get('expiryDate')?.value ||
        this.tabItem.legs[0].leg.leg.preSetPosition.expiryDate;
    } else {
      hedgeType = 'forward';
    }
    if (expiryDate) {
      this.showExpiryDate = true;
      expiryDate = reformatDate(expiryDate, 'YYYY-MM-DD');
    }

    return this.tabItem.isVolBased
      ? []
      : [
          {
            productType: hedgeType,
            settlementDate,
            expiryDate,
            strike:
              quote.request.hedgeRate ||
              (strikeCtrl?.dirty && Utilities.isNotBlank(strikeCtrl.value)
                ? cleanFormattedNumeric(strikeCtrl.value)
                : undefined),
            notional:
              notionalCtrl?.dirty && notionalCtrl.value
                ? cleanFormattedNumeric(notionalCtrl.value)
                : undefined,
            position: notionalCtrl?.dirty ? positionCtrl.value : undefined
          }
        ];
  }

  private callVolBasedRfqOptionOnLegChange(): void {
    if (this.tabItem.isPremiumBasedVanillasWithSpotFeed) {
      const subs = this.legComponents
        .toArray()
        .map(leg => leg.form.valueChanges.pipe(debounceTime(1000)));
      merge(...subs).subscribe({
        next: _ => this.volBasedRfqOptionPrice()
      });
    }
  }

  private volBasedRfqOptionPrice(): void {
    if (!this.setupForm?.valid) {
      return;
    }

    const legComponents = this.legComponents.toArray();
    const allowPricing =
      !this.blockPricing &&
      !legComponents.find(leg => {
        const forwardPointsBidCtrl = leg.form.get('forwardPointsBid');
        const forwardPointsAskCtrl = leg.form.get('forwardPointsAsk');
        const dfCurrencyCtrl = leg.form.get('discountFactorCurrency');
        const fwdPointBid = forwardPointsBidCtrl
          ? Utilities.cleanFormattedNumeric(forwardPointsBidCtrl.value)
          : null;

        const fwdPointAsk = forwardPointsAskCtrl
          ? Utilities.cleanFormattedNumeric(forwardPointsAskCtrl.value)
          : null;

        const dfCurrency = dfCurrencyCtrl?.value;
        return (
          !leg.form.valid ||
          leg.form.pending ||
          (forwardPointsBidCtrl &&
            forwardPointsAskCtrl &&
            +fwdPointBid > +fwdPointAsk) ||
          (dfCurrencyCtrl && !dfCurrency)
        );
      });

    if (allowPricing) {
      const volBasedRfqOptionPrice = this.getVolBasedRfqOptionPrice();
      if (volBasedRfqOptionPrice) {
        const payloadNotEqual = !isEqual(
          volBasedRfqOptionPrice,
          this.prevVolBasedRfqOptionPrice
        );
        if (payloadNotEqual) {
          this.prevVolBasedRfqOptionPrice = volBasedRfqOptionPrice;
          this.volBasedRfqOptionPriceSub?.unsubscribe();
          this.pricesLoading = true;
          this.volBasedRfqOptionPriceSub = this.rfqService
            .tradeService<VolBasedRfqOptionPriceV2Result>(
              volBasedRfqOptionPrice
            )
            .subscribe({
              next: (response: VolBasedRfqOptionPriceV2Result) => {
                this.pricesLoading = false;
                this.initialPrice = false;
                this.volBasedRfqOptionPriceResult = response;
              },
              error: _ => {
                this.pricesLoading = false;
              }
            });
        }
      }
    }
  }

  private createSetupForm(): void {
    let ctrls: Record<string, FormControl | FormGroup | FormArray> = {
      hedges: new FormArray([])
    };
    if (this.tabItem.isPremiumBasedVanillasWithSpotFeed) {
      const spotReg = '^\\d+(?:.\\d+)?$';
      ctrls = {
        ...ctrls,
        spots: new FormGroup(
          {
            spotAsk: new FormControl(null, [
              Validators.required,
              Validators.pattern(spotReg)
            ]),
            spotBid: new FormControl(null, [
              Validators.required,
              Validators.pattern(spotReg)
            ])
          },
          this.spotsValidator
        )
      };
    }
    this.setupForm = new FormGroup(ctrls);
  }

  private async getExecutionContext(): Promise<ExecutionContext> {
    if (!this.rfqDetails) {
      await sleep(2000);
    }
    if (!this.rfqDetails) {
      console.warn('Missing RFQ Details (after sleep)', clone(this.rfqDetails));
    }
    // The "client" in the request is typically populated with dummy/empty values
    // so take the client from the response if available
    const quoteEvent = {
      ...(this.rfqDetails?.medusaRequest || {}),
      client:
        this.rfqDetails?.medusaResponse?.result?.client ||
        this.rfqDetails?.medusaRequest?.client ||
        {}
    };
    return firstValueFrom(
      this.rfqService.getExecutionContext({
        task: 'getExecutionContext',
        products: this.tabItem.originalLegs,
        isProvider: true,
        quoteEvent
      })
    );
  }

  private spotsValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    const val = control.value;
    if (val.spotAsk >= val.spotBid) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  private handleQuoteEvent(legs: Record<string, any>[]): LegArray {
    return legs.map(row => {
      let productSpec = this.legService.getProductSpec(null, row);

      const extraAttributesOptions = {
        productSpec,
        isVolBased: this.tabItem.isVolBased,
        addDf:
          (this.tabItem.isPremiumBasedVanillasWithSpotFeed ||
            this.tabItem.isVolBased) &&
          canDisplayDF(productSpec.productType.shortName),
        isProviderQuote: true,

        userSettings: this.configService.userSettings(),
        removeSpot: true,
        addWhiteLabelAttributes: false
      };
      productSpec =
        this.tabItem.isPremiumBasedVanillasWithSpotFeed ||
        this.tabItem.isVolBased
          ? this.productsConfigService.addExtraRfqAttributes(
              extraAttributesOptions
            )
          : productSpec;

      productSpec =
        this.productsConfigService.addExtraAttributeValidators(productSpec);
      const leg: Leg = this.legService.makeLeg(productSpec, row, false, false);
      const legItem: LegArrayItem = {
        leg,
        form: this.attributeControlService.toFormGroup(
          leg.attributesAsFormControls
        ),
        fieldsToBeEdited: null
      };
      return legItem;
    });
  }

  private getWhiteLabelLastClientQuoteEvent(): void {
    if (this.rfqDetails?.is_wl && !this.wlQuoteEventsSub) {
      this.wlQuoteEventsSub = this.providerQuoteService
        .getWhiteLabelLastClientQuoteEvent(
          this.tabItem.requestId,
          this.rfqDetails.clientProvider.shortName
        )
        .pipe(repeat({ delay: 1000 }))
        .subscribe(x => {
          if (x.response.event) {
            this.rfqTabService.handleEvents([x.response.event]);
          }
        });
      this.subSink.sink = this.wlQuoteEventsSub;
    }
  }

  private liquidityProviderEvents(): void {
    let liquidityProviderTimestamp = 0;
    if (this.tabItem.isVolBased) {
      return;
    }
    const requestId = this.tabItem?.fullQuote.quote.request.requestId;
    this.lpEventsSub = this.providerQuoteService
      .getLiquidityProviderEvents([requestId], liquidityProviderTimestamp)
      .pipe(
        tap(lpResponse => {
          if (!Object.keys(lpResponse.response).length) {
            return;
          }
          liquidityProviderTimestamp = this.handleQuotes(
            lpResponse,
            liquidityProviderTimestamp
          );
        }),
        switchMap(_ => {
          return liquidityProviderTimestamp === 0
            ? EMPTY
            : of(null).pipe(
                switchMap(_ =>
                  this.providerQuoteService.getLiquidityProviderEvents(
                    [requestId],
                    liquidityProviderTimestamp
                  )
                ),
                tap(lpResponse => {
                  liquidityProviderTimestamp = this.handleQuotes(
                    lpResponse,
                    liquidityProviderTimestamp
                  );
                }),
                repeat({ delay: 1000 })
              );
        })
      )
      .subscribe();
    this.subSink.sink = this.lpEventsSub;
  }

  private handleQuotes(
    lpResponse: GetLiquidityProviderEventsResponse,
    liquidityProviderTimestamp: number
  ): number {
    if (!liquidityProviderTimestamp) {
      this.rfqTabService.setForwardPointsMultiplier();

      this.forwardPointsMultiplier =
        this.rfqTabService.forwardPointsMultiplier();

      this.rfqTabService.rfqDetailsDTO = lpResponse.response.result.find(
        event => event['@type'] === 'RFQDetailsDTO'
      );
    }

    const events = lpResponse.response.result.filter(
      event =>
        event.action === 'ConfirmQuoteSubmit' ||
        event['@type'] === 'QuoteRankEvent'
    );

    this.rfqTabService.handleEvents(events);
    return lpResponse.response.timestamp;
  }

  private rfqDetailsChange(): void {
    this.rfqTabService.rfqDetailsDTO = this.tabItem.rfqDetails?.RFQDetailsDTO;
    this.getWhiteLabelLastClientQuoteEvent();
  }

  private setupProviderTab(): void {
    this.rfqTabService.tabData = this.tabItem;
    Object.assign(
      this.tabItem,
      this.providerQuoteService.updateTab(
        this.tabItem.fullQuote,
        this.handleQuoteEvent(this.tabItem.originalLegs)
      )
    );
    this.legService.productSpecifications = clone(
      this.productsConfigService.productInfo.productSpecifications
    );
    const productTypeForLeg0 =
      this.tabItem.legs[0].leg.leg.preSetPosition.productType;

    this.legService.attributesDisplayList =
      this.productsConfigService.addExtraRfqAttributesToList(
        this.tabItem.isPremiumBasedVanillasWithSpotFeed,
        this.tabItem.isVolBased,
        productTypeForLeg0,
        this.configService.userSettings(),
        false
      );
    this.legService.tradeAttributes =
      this.productsConfigService.productInfo.tradeAttributes;
    this.handlePremiumBasedWithSpotFeed();
    this.handleEditableFields();
    this.legs = this.tabItem.legs;
    this.nestedLegs = this.legs.map(leg => leg.leg.leg);
    this.legs.forEach(leg => {
      this.legService.updateActiveAttributes('add', leg.leg.leg, false);
    });
    this.cross =
      this.tabItem.title.quotePair.base + this.tabItem.title.quotePair.term;
    this.createSetupForm();
    this.liquidityProviderEvents();
    const containsDcd = this.legs.find(
      rfqLeg => rfqLeg.leg?.leg?.productType === DUAL_CURRENCY_DEPOSIT
    );
    this.tabItem.useWhiteLabelMargin =
      this.configService.userSettings().useWhiteLabelMargin && !containsDcd;
    this.showTab = true;
  }
}
